import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext'; 
import { useFilters } from '../context/FilterContext';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Container,
  Chip,
  FormControl,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as ReferralIcon,
  Visibility as EyeIcon,
  DirectionsWalk as FootIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Business as BusinessIcon, 
  CalendarToday,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import Layout from '../components/Layout';
import FilterBar from '../components/FilterBar';
import { dashboardAPI } from '../services/api';
import DRClassificationReport from '../components/DRClassificationReport';
import {
  TopPerformersCard,
  AlertsWidget,
  InsightsCard,
  BenchmarkCard,
} from '../components/AnalyticsCards';

const Dashboard = () => {
  const { user } = useAuth(); 
  const { filters } = useFilters();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState({});
  const [facilityComparison, setFacilityComparison] = useState([]);
  const [chartData, setChartData] = useState({
    thiredGraphs: { Total: [], Foot_Care_Cases: [], Diabetic_Retinopathy_Cases: [], categories: [] },
    fourthGraphs: { Foot_Care_Cases: [], Diabetic_Retinopathy_Cases: [], categories: [] },
    fifthGraphs: { valuesToDisplay: [], categories: [], Total_Cases_For_Foot: 0 },
    sixthGraphs: { valuesToDisplay: [], categories: [], Total_Cases_For_eye: 0 },
    seventhGraphs: { valuesToDisplay: [], categories: [] },
    eighthGraphs: { valuesToDisplay: [], categories: [] }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [performance, setPerformance] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [insights, setInsights] = useState([]);
  const [benchmark, setBenchmark] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Year comparison state
  const [yearComparisonMode, setYearComparisonMode] = useState(false);
  const [selectedChartYear, setSelectedChartYear] = useState('');
  const [comparisonData, setComparisonData] = useState({});
  const [monthlyComparisonData, setMonthlyComparisonData] = useState({}); // ✅ NEW

  const isFacilityUser = !!user?.facility_name;

  useEffect(() => {
    loadAllData();
  }, [filters, yearComparisonMode, selectedChartYear]);

  const aggregateMonthlyData = (rawData) => {
    const monthlyAggregated = {};
    
    rawData.forEach(item => {
      const monthNum = item.month_num;
      
      if (!monthlyAggregated[monthNum]) {
        monthlyAggregated[monthNum] = {
          month_name: item.month_name,
          eye_cases: 0,
          foot_cases: 0,
          eye_referred: 0,
          foot_referred: 0,
        };
      }
      
      monthlyAggregated[monthNum].eye_cases += parseInt(item.eye_cases || 0);
      monthlyAggregated[monthNum].foot_cases += parseInt(item.foot_cases || 0);
      monthlyAggregated[monthNum].eye_referred += parseInt(item.eye_referred || 0);
      monthlyAggregated[monthNum].foot_referred += parseInt(item.foot_referred || 0);
    });
    
    const sortedMonths = Object.keys(monthlyAggregated).sort((a, b) => a - b);
    
    return {
      months: sortedMonths.map(m => monthlyAggregated[m].month_name),
      eye_cases: sortedMonths.map(m => monthlyAggregated[m].eye_cases),
      foot_cases: sortedMonths.map(m => monthlyAggregated[m].foot_cases),
      eye_referred: sortedMonths.map(m => monthlyAggregated[m].eye_referred),
      foot_referred: sortedMonths.map(m => monthlyAggregated[m].foot_referred),
    };
  };

  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const effectiveYear = selectedChartYear || filters.year || '';
      
      const filterParams = {
        region: filters.region || undefined,
        facility_name: filters.facility_name || (isFacilityUser ? user?.facility_name : undefined),
        facility_type: filters.facility_type || undefined,
        year: effectiveYear,
        month_start: filters.month_from || undefined,
        month_end: filters.month_to || undefined,
        gender: filters.gender || undefined,
        form_type: filters.form_type || undefined,
      };

      const cleanParams = Object.fromEntries(
        Object.entries(filterParams).filter(([_, v]) => v !== undefined && v !== '')
      );

      console.log('📊 Loading dashboard with params:', cleanParams);

      const [
        filteredResponse, 
        monthlyResponse, 
        facilityResponse,
        performanceRes,
        alertsRes,
        trendsRes,
        notificationsRes,
      ] = await Promise.all([
        dashboardAPI.getDashboardDataFiltered(cleanParams),
        dashboardAPI.getMonthlyTrends(cleanParams),
        dashboardAPI.getFacilityComparison(cleanParams),
        dashboardAPI.getFacilityPerformance(cleanParams),
        dashboardAPI.getHighRiskAlerts(cleanParams),
        dashboardAPI.getTrendAnalysis(cleanParams),
        dashboardAPI.getDashboardNotifications(cleanParams),
      ]);

      // ✅ FIXED: Load year comparison data
      if (yearComparisonMode && !selectedChartYear && !filters.facility_name) {
        const currentYearNum = new Date().getFullYear();
        const years = [currentYearNum, currentYearNum - 1, currentYearNum - 2];
        
        console.log('📊 Loading comparison for years:', years);
        
        const yearDataPromises = years.map(year => 
          dashboardAPI.getDashboardDataFiltered({ 
            ...cleanParams, 
            year: year.toString(),
            region: cleanParams.region || undefined
          })
        );
        
        // ✅ NEW: Load monthly trends for comparison
        const monthlyYearPromises = years.map(year =>
          dashboardAPI.getMonthlyTrends({ ...cleanParams, year: year.toString() })
        );
        
        const [yearResults, monthlyYearResults] = await Promise.all([
          Promise.all(yearDataPromises),
          Promise.all(monthlyYearPromises)
        ]);
        
        const comparison = {};
        const monthlyComparison = {};
        
        years.forEach((year, idx) => {
          const apiData = yearResults[idx].data?.data || {};
          const regionData = apiData.region_data || {};
          
          comparison[year] = {
            thiredGraphs: regionData.thiredGraphs || {
              Total: [0, 0, 0, 0],
              Foot_Care_Cases: [0, 0, 0, 0],
              Diabetic_Retinopathy_Cases: [0, 0, 0, 0]
            },
            fourthGraphs: regionData.fourthGraphs || {
              Foot_Care_Cases: [0, 0, 0, 0],
              Diabetic_Retinopathy_Cases: [0, 0, 0, 0]
            }
          };
          
          // ✅ NEW: Process monthly comparison data
          const monthlyRaw = monthlyYearResults[idx].data?.raw_data || [];
          monthlyComparison[year] = aggregateMonthlyData(monthlyRaw);
        });
        
        setComparisonData(comparison);
        setMonthlyComparisonData(monthlyComparison);
        console.log('✅ Comparison data loaded:', comparison);
        console.log('✅ Monthly comparison loaded:', monthlyComparison);
      }
      
      const apiData = filteredResponse.data?.data || {};
      
      const transformedData = {
        totalTests: parseInt(apiData.total_cases_screened || 0),
        totalFacilities: parseInt(apiData.total_facilities || 0),
        eyeFormsCount: parseInt(apiData.total_eye_cases || 0),
        footFormsCount: parseInt(apiData.total_foot_cases || 0),
        eyeReferredCases: parseInt(apiData.diabetic_retinopathy_cases_referred || 0),
        footReferredCases: parseInt(apiData.foot_care_cases_referred || 0),
        maleCases: parseInt(apiData.male_cases || 0),
        femaleCases: parseInt(apiData.female_cases || 0),
        eyeMaleCases: parseInt(apiData.eye_male_cases || 0),
        eyeFemaleCases: parseInt(apiData.eye_female_cases || 0),
        footMaleCases: parseInt(apiData.foot_male_cases || 0),
        footFemaleCases: parseInt(apiData.foot_female_cases || 0),
        eyeReferredMale: parseInt(apiData.eye_referred_male || 0),
        eyeReferredFemale: parseInt(apiData.eye_referred_female || 0),
        footReferredMale: parseInt(apiData.foot_referred_male || 0),
        footReferredFemale: parseInt(apiData.foot_referred_female || 0),
        footAccepted: parseInt(apiData.foot_accepted || 0),
        footRejected: parseInt(apiData.foot_rejected || 0),
        footPending: parseInt(apiData.foot_pending || 0),
        eyeAccepted: parseInt(apiData.eye_accepted || 0),
        eyeRejected: parseInt(apiData.eye_rejected || 0),
        eyePending: parseInt(apiData.eye_pending || 0),
        footRiskLow: parseInt(apiData.foot_low_risk || 0),
        footRiskModerate: parseInt(apiData.foot_moderate_risk || 0),
        footRiskHigh: parseInt(apiData.foot_high_risk || 0),
        eyeRiskNone: parseInt(apiData.eye_no_risk || 0),
        eyeRiskLow: parseInt(apiData.eye_low_risk || 0),
        eyeRiskModerate: parseInt(apiData.eye_moderate_risk || 0),
        eyeRiskHigh: parseInt(apiData.eye_high_risk || 0),
      };
      
      setDashboardData(transformedData);
      setPerformance(performanceRes.data?.performance_data || []);
      setAlerts(alertsRes.data || null);
      setInsights(trendsRes.data?.insights || []);
      setNotifications(notificationsRes.data?.notifications || []);

      if (cleanParams.facility_name) {
        try {
          const benchmarkRes = await dashboardAPI.getBenchmarkComparison(
            cleanParams.facility_name,
            { year: effectiveYear || new Date().getFullYear().toString() }
          );
          setBenchmark(benchmarkRes.data?.benchmark);
        } catch (err) {
          setBenchmark(null);
        }
      } else {
        setBenchmark(null);
      }
      
      const totalTests = transformedData.totalTests;
      
      if (totalTests > 0) {
        const regionData = filteredResponse.data.data?.region_data;

        setChartData({
          thiredGraphs: regionData?.thiredGraphs || {
            Total: [0, 0, 0, 0],
            Foot_Care_Cases: [0, 0, 0, 0],
            Diabetic_Retinopathy_Cases: [0, 0, 0, 0],
            categories: ['Hhohho', 'Lubombo', 'Manzini', 'Shiselweni']
          },
          fourthGraphs: regionData?.fourthGraphs || {
            Foot_Care_Cases: [0, 0, 0, 0],
            Diabetic_Retinopathy_Cases: [0, 0, 0, 0],
            categories: ['Hhohho', 'Lubombo', 'Manzini', 'Shiselweni']
          },
          fifthGraphs: {
            valuesToDisplay: [
              transformedData.footAccepted,
              transformedData.footRejected,
              transformedData.footPending,
            ],
            categories: ['Accepted Cases', 'Rejected Cases', 'Pending Cases'],
            Total_Cases_For_Foot: transformedData.foot_total_referred || 0 
          },
          sixthGraphs: {
            valuesToDisplay: [
              transformedData.eyeAccepted,
              transformedData.eyeRejected,
              transformedData.eyePending,
            ],
            categories: ['Accepted Cases', 'Rejected Cases', 'Pending Cases'],
            Total_Cases_For_eye: transformedData.eye_total_referred || 0
          },
          seventhGraphs: {
            valuesToDisplay: [
              transformedData.footRiskLow,
              transformedData.footRiskModerate,
              transformedData.footRiskHigh
            ],
            categories: ['Low Risk', 'Moderate Risk', 'High Risk']
          },
          eighthGraphs: {
            valuesToDisplay: [
              transformedData.eyeRiskNone,
              transformedData.eyeRiskLow,
              transformedData.eyeRiskModerate,
              transformedData.eyeRiskHigh
            ],
            categories: ['No Risk', 'Low Risk', 'Moderate Risk', 'High Risk']
          },
        });
      }
      
      const monthlyRawData = monthlyResponse.data?.raw_data || [];
      const aggregatedMonthly = aggregateMonthlyData(monthlyRawData);
      setMonthlyTrends(aggregatedMonthly);
      
      const facilityRawData = facilityResponse.data?.raw_data || [];
      setFacilityComparison(facilityRawData);
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ChartHeader = ({ title, showYearFilter = true, showComparison = false }) => {
    const availableYears = ['2022', '2023', '2024', '2025', '2026'];
    
    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
          {title}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          {showComparison && (
            <ToggleButtonGroup
              value={yearComparisonMode}
              exclusive
              onChange={(e, val) => {
                if (val !== null) {
                  setYearComparisonMode(val);
                  if (val) {
                    setSelectedChartYear('');
                  }
                }
              }}
              size="small"
            >
              <ToggleButton value={false} sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}>
                SINGLE YEAR
              </ToggleButton>
              <ToggleButton value={true} sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}>
                <CompareIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                COMPARE YEARS
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {showYearFilter && !yearComparisonMode && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedChartYear || filters.year || ''}
                onChange={(e) => setSelectedChartYear(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.85rem' }}
              >
                <MenuItem value="" sx={{ fontSize: '0.85rem' }}>Current Filters</MenuItem>
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year} sx={{ fontSize: '0.85rem' }}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>
    );
  };

  const CompactStatCard = ({ title, value, icon, gradient, maleCount, femaleCount }) => (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
        }
      }}
    >
      <Box sx={{ height: 4, background: gradient }} />
      <CardContent sx={{ p: 2, pb: '16px !important' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#64748b', 
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.3px',
                display: 'block',
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 800,
                color: '#1e293b',
                fontSize: '1.8rem',
                lineHeight: 1,
                mb: 1,
              }}
            >
              {value || 0}
            </Typography>

            {(maleCount !== undefined || femaleCount !== undefined) && (
              <Box display="flex" gap={1} mt={1}>
                {maleCount !== undefined && (
                  <Chip
                    icon={<MaleIcon sx={{ fontSize: 14 }} />}
                    label={maleCount}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      '& .MuiChip-icon': { color: '#1e40af' },
                    }}
                  />
                )}
                {femaleCount !== undefined && (
                  <Chip
                    icon={<FemaleIcon sx={{ fontSize: 14 }} />}
                    label={femaleCount}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: '#fce7f3',
                      color: '#be185d',
                      '& .MuiChip-icon': { color: '#be185d' },
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 22, color: 'white' } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="70vh">
          <CircularProgress size={64} thickness={4} sx={{ color: '#667eea', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500 }}>
            Loading Dashboard Data...
          </Typography>
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(239, 68, 68, 0.1)' }}>
            {error}
          </Alert>
        </Container>
      </Layout>
    );
  }

  const chartColors = {
    gold: '#f59e0b',
    brown: '#92400e',
    darkBlue: '#374151',
    purple: '#8b5cf6',
    teal: '#14b8a6',
    pink: '#ec4899',
  };

  const baseBarChartOptions = {
    chart: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      toolbar: { 
        show: true,
        tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false }
      },
    },
    dataLabels: { enabled: false },
    grid: { borderColor: '#f1f5f9', strokeDashArray: 3 },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      labels: { colors: '#475569', useSeriesColors: false },
      markers: { radius: 12 },
    },
    tooltip: {
      y: { formatter: (val) => `${val} cases` },
      theme: 'light',
      style: { fontSize: '13px' },
    },
  };

  // ✅ FIXED: Year comparison for first chart
  const currentYearNum = new Date().getFullYear();

  const regionComparisonSeries = yearComparisonMode ? [
    {
      name: `Total Cases ${currentYearNum}`,
      data: comparisonData[currentYearNum]?.thiredGraphs?.Total || [0, 0, 0, 0],
    },
    {
      name: `Total Cases ${currentYearNum - 1}`,
      data: comparisonData[currentYearNum - 1]?.thiredGraphs?.Total || [0, 0, 0, 0],
    },
    {
      name: `Total Cases ${currentYearNum - 2}`,
      data: comparisonData[currentYearNum - 2]?.thiredGraphs?.Total || [0, 0, 0, 0],
    },
  ] : [
    {
      name: 'Total Cases',
      data: chartData.thiredGraphs.Total || [],
    },
    {
      name: 'Foot Care Cases',
      data: chartData.thiredGraphs.Foot_Care_Cases || [],
    },
    {
      name: 'Diabetic Retinopathy Cases',
      data: chartData.thiredGraphs.Diabetic_Retinopathy_Cases || [],
    },
  ];

  const regionComparisonOptions = {
    ...baseBarChartOptions,
    chart: { ...baseBarChartOptions.chart, type: 'bar', height: 380 },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: yearComparisonMode ? '85%' : '65%',
        borderRadius: 6,
        borderRadiusApplication: 'end',
      },
    },
    colors: yearComparisonMode 
      ? ['#f59e0b', '#92400e', '#374151'] 
      : [chartColors.gold, chartColors.brown, chartColors.darkBlue],
    stroke: { show: true, width: 2, colors: ['transparent'] },
    xaxis: {
      categories: chartData.thiredGraphs.categories || [],
      labels: { style: { colors: '#64748b', fontSize: '12px', fontWeight: 600 } }
    },
    yaxis: {
      title: { text: 'No of Cases', style: { color: '#475569', fontSize: '13px', fontWeight: 600 } },
      labels: { style: { colors: '#64748b' } }
    },
  };

  // ✅ FIXED: Year comparison for second chart
  const regionReferredSeries = yearComparisonMode ? [
    {
      name: `Foot Referred ${currentYearNum}`,
      data: comparisonData[currentYearNum]?.fourthGraphs?.Foot_Care_Cases || [0, 0, 0, 0],
    },
    {
      name: `Eye Referred ${currentYearNum}`,
      data: comparisonData[currentYearNum]?.fourthGraphs?.Diabetic_Retinopathy_Cases || [0, 0, 0, 0],
    },
    {
      name: `Foot Referred ${currentYearNum - 1}`,
      data: comparisonData[currentYearNum - 1]?.fourthGraphs?.Foot_Care_Cases || [0, 0, 0, 0],
    },
    {
      name: `Eye Referred ${currentYearNum - 1}`,
      data: comparisonData[currentYearNum - 1]?.fourthGraphs?.Diabetic_Retinopathy_Cases || [0, 0, 0, 0],
    },
  ] : [
    {
      name: 'Foot Care Referred',
      data: chartData.fourthGraphs.Foot_Care_Cases || [],
    },
    {
      name: 'Diabetic Retinopathy Referred',
      data: chartData.fourthGraphs.Diabetic_Retinopathy_Cases || [],
    },
  ];

  const regionReferredOptions = {
    ...baseBarChartOptions,
    chart: { ...baseBarChartOptions.chart, type: 'bar', height: 380 },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: yearComparisonMode ? '85%' : '65%',
        borderRadius: 6,
        borderRadiusApplication: 'end',
      },
    },
    colors: yearComparisonMode 
      ? ['#92400e', '#374151', '#d97706', '#6b7280']
      : [chartColors.brown, chartColors.darkBlue],
    stroke: { show: true, width: 2, colors: ['transparent'] },
    xaxis: {
      categories: chartData.fourthGraphs.categories || [],
      labels: { style: { colors: '#64748b', fontSize: '12px', fontWeight: 600 } }
    },
    yaxis: {
      title: { text: 'No of Cases', style: { color: '#475569', fontSize: '13px', fontWeight: 600 } },
      labels: { style: { colors: '#64748b' } }
    },
  };

  const footStatusOptions = {
    chart: { type: 'donut', height: 380, fontFamily: "'Inter', sans-serif" },
    labels: chartData.fifthGraphs.categories || [],
    colors: ['#10b981', '#ef4444', '#f59e0b'],
    legend: {
      position: 'bottom',
      labels: { colors: '#475569', useSeriesColors: false },
      markers: { radius: 12 },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: { fontSize: '14px', fontWeight: 700, colors: ['#fff'] },
      dropShadow: { enabled: true, blur: 2, opacity: 0.8 },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: { fontSize: '14px', fontWeight: 600, color: '#475569' },
            value: { fontSize: '24px', fontWeight: 700, color: '#1e293b' },
            total: {
              show: true,
              label: 'Referred Foot Cases',
              fontSize: '14px',
              fontWeight: 600,
              color: '#475569',
            }
          }
        }
      }
    },
    stroke: { width: 2, colors: ['#fff'] },
    tooltip: { y: { formatter: (val) => `${val} cases` } },
  };

  const footStatusSeries = chartData.fifthGraphs.valuesToDisplay || [];

  const eyeStatusOptions = {
    ...footStatusOptions,
    labels: chartData.sixthGraphs.categories || [],
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: { fontSize: '14px', fontWeight: 600, color: '#475569' },
            value: { fontSize: '24px', fontWeight: 700, color: '#1e293b' },
            total: {
              show: true,
              label: 'Referred Eye Cases',
              fontSize: '14px',
              fontWeight: 600,
              color: '#475569',
            }
          }
        }
      }
    },
  };

  const eyeStatusSeries = chartData.sixthGraphs.valuesToDisplay || [];

  const footRiskOptions = {
    ...baseBarChartOptions,
    chart: { ...baseBarChartOptions.chart, type: 'bar', height: 380 },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 8, barHeight: '65%', distributed: true },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '13px', fontWeight: 700, colors: ['#fff'] },
    },
    colors: ['#10b981', '#f59e0b', '#ef4444'],
    xaxis: {
      categories: chartData.seventhGraphs.categories || [],
      labels: { style: { colors: '#64748b', fontSize: '11px', fontWeight: 600 } }
    },
    yaxis: {
      labels: { style: { colors: '#1e293b', fontSize: '12px', fontWeight: 600 } }
    },
    legend: { show: false },
  };

  const footRiskSeries = [{ name: 'Cases', data: chartData.seventhGraphs.valuesToDisplay || [] }];

  const eyeRiskOptions = {
    ...footRiskOptions,
    colors: ['#667eea', '#10b981', '#f59e0b', '#ef4444'],
    xaxis: {
      categories: chartData.eighthGraphs.categories || [],
      labels: { style: { colors: '#64748b', fontSize: '11px', fontWeight: 600 } }
    },
  };

  const eyeRiskSeries = [{ name: 'Cases', data: chartData.eighthGraphs.valuesToDisplay || [] }];

  const monthlyTrendsOptions = {
    chart: { type: 'line', height: 380, toolbar: { show: true }, fontFamily: "'Inter', sans-serif" },
    stroke: { curve: 'smooth', width: 3 },
    colors: [chartColors.gold, chartColors.brown, chartColors.teal, chartColors.pink],
    markers: { size: 5, strokeWidth: 2, strokeColors: '#fff', hover: { size: 7 } },
    xaxis: {
      categories: monthlyTrends.months || [],
      labels: { style: { colors: '#64748b', fontSize: '11px', fontWeight: 600 }, rotate: -45 }
    },
    yaxis: {
      title: { text: 'Number of Cases', style: { color: '#475569', fontSize: '13px', fontWeight: 600 } },
      labels: { style: { colors: '#64748b' } }
    },
    grid: { borderColor: '#f1f5f9', strokeDashArray: 3 },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      labels: { colors: '#475569' },
      markers: { radius: 12 },
    },
    tooltip: { y: { formatter: (val) => `${val} cases` } },
  };

  const monthlyTrendsSeries = yearComparisonMode ? [
    { name: `Eye Tests ${currentYearNum}`, data: monthlyComparisonData[currentYearNum]?.eye_cases || [] },
    { name: `Foot Tests ${currentYearNum}`, data: monthlyComparisonData[currentYearNum]?.foot_cases || [] },
    { name: `Eye Tests ${currentYearNum - 1}`, data: monthlyComparisonData[currentYearNum - 1]?.eye_cases || [] },
    { name: `Foot Tests ${currentYearNum - 1}`, data: monthlyComparisonData[currentYearNum - 1]?.foot_cases || [] },
  ] : [
    { name: 'Eye Tests', data: monthlyTrends.eye_cases || [] },
    { name: 'Foot Tests', data: monthlyTrends.foot_cases || [] },
    { name: 'Referred Eye', data: monthlyTrends.eye_referred || [] },
    { name: 'Referred Foot', data: monthlyTrends.foot_referred || [] },
  ];

  const facilityComparisonOptions = {
    ...baseBarChartOptions,
    chart: { ...baseBarChartOptions.chart, type: 'bar', height: 450 },
    plotOptions: {
      bar: { horizontal: false, columnWidth: '80%', borderRadius: 6, borderRadiusApplication: 'end' },
    },
    colors: [chartColors.gold, chartColors.brown, chartColors.teal, chartColors.pink],
    xaxis: {
      categories: facilityComparison.map(f => f.facility_name) || [],
      labels: {
        style: { colors: '#64748b', fontSize: '10px', fontWeight: 600 },
        rotate: -45,
        rotateAlways: true,
      }
    },
    yaxis: {
      title: { text: 'Number of Cases', style: { color: '#475569', fontSize: '13px', fontWeight: 600 } },
      labels: { style: { colors: '#64748b' } }
    },
  };

  const facilityComparisonSeries = [
    { name: 'Eye Tests', data: facilityComparison.map(f => parseInt(f.eye_cases || 0)) },
    { name: 'Foot Tests', data: facilityComparison.map(f => parseInt(f.foot_cases || 0)) },
    { name: 'Referred Eye', data: facilityComparison.map(f => parseInt(f.eye_referred || 0)) },
    { name: 'Referred Foot', data: facilityComparison.map(f => parseInt(f.foot_referred || 0)) },
  ];

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
              Welcome, {user?.first_name}!
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              {user?.facility_name 
                ? `Viewing data for ${user.facility_name}`
                : 'Viewing data for all facilities across Eswatini'
              }
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <CalendarToday sx={{ fontSize: 14, color: '#64748b' }} />
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Last updated: {new Date().toLocaleString()}
              </Typography>
            </Box>
          </Box>
          
         <FilterBar onApplyFilters={loadAllData} />

          {notifications && notifications.length > 0 && (
            <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 2 }}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <AssessmentIcon sx={{ color: '#f59e0b', mt: 0.5 }} />
                <Box flex={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                    {notifications.length} Active Notification{notifications.length > 1 ? 's' : ''}
                  </Typography>
                  {notifications.map((notif, idx) => (
                    <Typography key={idx} variant="body2" sx={{ color: '#92400e', mb: 0.5 }}>
                      • {notif.message}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Paper>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2}>
              <CompactStatCard
                title="Total Tests"
                value={dashboardData?.totalTests}
                icon={<AssessmentIcon />}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                maleCount={dashboardData?.maleCases}
                femaleCount={dashboardData?.femaleCases}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <CompactStatCard
                title="Total Facilities"
                value={dashboardData?.totalFacilities}
                icon={<BusinessIcon />}
                gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <CompactStatCard
                title="Diabetic Retinopathy"
                value={dashboardData?.eyeFormsCount}
                icon={<EyeIcon />}
                gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                maleCount={dashboardData?.eyeMaleCases}
                femaleCount={dashboardData?.eyeFemaleCases}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <CompactStatCard
                title="Foot Screening"
                value={dashboardData?.footFormsCount}
                icon={<FootIcon />}
                gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                maleCount={dashboardData?.footMaleCases}
                femaleCount={dashboardData?.footFemaleCases}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <CompactStatCard
                title="Eye Referred"
                value={dashboardData?.eyeReferredCases}
                icon={<ReferralIcon />}
                gradient="linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)"
                maleCount={dashboardData?.eyeReferredMale}
                femaleCount={dashboardData?.eyeReferredFemale}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <CompactStatCard
                title="Foot Referred"
                value={dashboardData?.footReferredCases}
                icon={<ReferralIcon />}
                gradient="linear-gradient(135deg, #ec4899 0%, #be185d 100%)"
                maleCount={dashboardData?.footReferredMale}
                femaleCount={dashboardData?.footReferredFemale}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <AlertsWidget 
                alerts={alerts} 
                loading={false}
                onViewAll={() => window.open('/high-risk-alerts', '_blank')}
              />
            </Grid>

            {insights && insights.length > 0 && (
              <Grid item xs={12} md={6}>
                <InsightsCard insights={insights} loading={false} />
              </Grid>
            )}

            {!isFacilityUser && performance && performance.length > 0 && (
              <Grid item xs={12} md={6}>
                <TopPerformersCard data={performance} loading={false} />
              </Grid>
            )}

            {benchmark && filters.facility_name && (
              <Grid item xs={12} md={6}>
                <BenchmarkCard benchmark={benchmark} loading={false} />
              </Grid>
            )}
          </Grid>

          {!isFacilityUser && !filters.facility_name && (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                    <ChartHeader 
                      title="Region-wise: Total Cases Vs Foot Care Vs Diabetic Retinopathy"
                      showYearFilter={true}
                      showComparison={true}
                    />
                    <ReactApexChart
                      options={regionComparisonOptions}
                      series={regionComparisonSeries}
                      type="bar"
                      height={380}
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                    <ChartHeader 
                      title="Region-wise: Referred Cases of Foot Care and Diabetic Retinopathy"
                      showYearFilter={true}
                      showComparison={true}
                    />
                    <ReactApexChart
                      options={regionReferredOptions}
                      series={regionReferredSeries}
                      type="bar"
                      height={380}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                <ChartHeader title="Foot Care Cases" showYearFilter={true} showComparison={false} />
                <ReactApexChart options={footStatusOptions} series={footStatusSeries} type="donut" height={380} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                <ChartHeader title="Diabetic Retinopathy" showYearFilter={true} showComparison={false} />
                <ReactApexChart options={eyeStatusOptions} series={eyeStatusSeries} type="donut" height={380} />
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                <ChartHeader title="Foot Care Risk Rating" showYearFilter={true} showComparison={false} />
                <ReactApexChart options={footRiskOptions} series={footRiskSeries} type="bar" height={380} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                <ChartHeader title="Diabetic Retinopathy Risk Rating" showYearFilter={true} showComparison={false} />
                <ReactApexChart options={eyeRiskOptions} series={eyeRiskSeries} type="bar" height={380} />
              </Paper>
            </Grid>
          </Grid>

          {monthlyTrends.months && monthlyTrends.months.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                  <ChartHeader title="Monthly Submission Trends" showYearFilter={true} showComparison={true} />
                  <ReactApexChart options={monthlyTrendsOptions} series={monthlyTrendsSeries} type="line" height={380} />
                </Paper>
              </Grid>
            </Grid>
          )}

          {!isFacilityUser && !filters.facility_name && facilityComparison && facilityComparison.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                  <ChartHeader title="Facility-wise Comparison" showYearFilter={true} showComparison={false} />
                  <ReactApexChart options={facilityComparisonOptions} series={facilityComparisonSeries} type="bar" height={450} />
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: 'white' }}>
                <ChartHeader title="DR Classification Report by Patient Type and Gender" showYearFilter={true} showComparison={false} />
                <DRClassificationReport filters={{ ...filters, year: selectedChartYear || filters.year }} />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Layout>
  );
};

export default Dashboard;
